import { User } from 'models/User';
import { ObjectValues } from 'types';

export type LeadBasicInfo = Pick<User, 'firstName' | 'lastName' | 'email'>;

export type HotLeadAggregationResult = LeadBasicInfo &
  Pick<User, 'avatarColor'> & {
    dataRoomMemberId: string;
    actionCount: number;
    viewCount: number;
    downloadCount: number;
    questionCount: number;
    lastSeen: Date;
    lastSessionDurationSeconds: number;
  };

export type ColdLeadAggregationResult = LeadBasicInfo &
  Pick<User, 'avatarColor'> & {
    dataRoomMemberId: string;
    lastSeen: Date | null;
  };

export type DataRoomVisitStat = {
  date: Date;
  visits: number;
  topVisitors?: LeadBasicInfo[];
};

export const DATA_ROOM_MEMBER_ACTIVITY_EVENT_TYPE = {
  VIEW_FILE: 'view_file',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  POST_QUESTION: 'pose_question',
} as const;

export type DataRoomMemberActivityEventType = ObjectValues<
  typeof DATA_ROOM_MEMBER_ACTIVITY_EVENT_TYPE
>;

export const DATA_ROOM_DASHBOARD_TIMEFRAME = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  SEVEN_DAYS: '7d',
  FOURTEEN_DAYS: '14d',
  THIRTY_DAYS: '30d',
  SIXTY_DAYS: '60d',
} as const;

export type DataRoomDashboardTimeframe = ObjectValues<
  typeof DATA_ROOM_DASHBOARD_TIMEFRAME
>;

export const HOT_LEAD_THRESHOLD = 3;

export type ColdLeadsBroadcastPayload = {
  message: string;
  sendInvitationEmail: boolean;
  dataRoomMemberIds: string[];
};

export const SUGGESTED_ACTION_TYPE = {
  DATA_ROOM_EMPTY: 'data_room_empty_start_uploading_files',
  NO_FILES_LAST_10_DAYS: 'no_files_added_in_last_10_days',
  UNREPLIED_QUESTIONS: 'unreplied_questions_in_data_room',
  NO_FAQS: 'no_faqs_in_data_room',
  PEOPLE_IN_WAITING_ROOM: 'peoples_in_waiting_room',
  SECURE_LINKS_EXPIRING: 'secure_links_expire',
  NEW_FILE_REQUEST: 'new_files_request_submitted',
  EMPTY_SECURE_LINKS: 'empty_secure_links',
  UNREAD_MESSAGES: 'unread_messages',
  UNREAD_LEAD_NOTES: 'unread_lead_notes',
  UNREAD_DEAL_NOTES: 'unread_deal_notes',
} as const;

export type SuggestedActionType = ObjectValues<typeof SUGGESTED_ACTION_TYPE>;

export type ActiveSuggestedAction = {
  type: SuggestedActionType;
  count?: number;
  names?: string[];
  expiredAt?: Date;
  shareableLinkId?: string;
  dataRoomQuestionId?: string;
  // Selling-portfolio deal room to navigate to (leads board) for the
  // unread messages / lead notes / deal notes actions.
  dealRoomId?: string;
  // Single unread message / lead note: the lead whose drawer should open.
  leadId?: string;
};

export type PausedSuggestedAction = ActiveSuggestedAction & {
  mutedUntil?: Date | null;
  mutedAt?: Date | null;
};

export type SuggestedActionsResponse = {
  active: ActiveSuggestedAction[];
  paused: PausedSuggestedAction[];
};

export const SUGGESTED_ACTION_MUTE_DAYS = 30;
